import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Link2 } from 'lucide-react';
import {
  COUNTRY_OPTIONS,
  type CandidateGroup,
  type CandidateItem,
  type ProductionEntityRef,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  fieldDescribedBy,
  useToast,
} from '@/components/ui';
import {
  classifyProductionRecord,
  confirmProductionLinks,
  leaveProductionUnresolved,
  listPharmacies,
  rejectProductionLinks,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  CATEGORY_LABELS,
  CONFIDENCE_LABELS,
  CONFIDENCE_TONES,
  ENTITY_TYPE_LABELS,
  SYSTEM_LABELS,
  itemKey,
  maskId,
} from './labels';

type ConfirmMode = 'create' | 'existing' | null;
type ClassifyTarget = 'test' | 'developer' | null;

interface CandidateReviewPanelProps {
  group: CandidateGroup;
  onClose: () => void;
  onChanged: () => void;
}

function SelectionRow({
  item,
  checked,
  disabled,
  lowReason,
  onToggle,
  onLowReason,
  canLink,
}: {
  item: CandidateItem;
  checked: boolean;
  disabled: boolean;
  lowReason: string;
  onToggle: (next: boolean) => void;
  onLowReason: (value: string) => void;
  canLink: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const isConflict = item.confidence === 'conflict';
  const needsReason = item.confidence === 'low' && checked;

  return (
    <div className={cn('rounded-lg border border-line px-3 py-3', isConflict && 'border-danger/30 bg-danger-soft/30')}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 rounded border-line-strong accent-[rgb(var(--color-primary))] disabled:cursor-not-allowed"
          checked={checked}
          disabled={disabled || isConflict || !canLink}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Select ${item.label}`}
          title={isConflict ? 'Conflict identities cannot be confirmed until resolved' : undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{SYSTEM_LABELS[item.ref.system]}</Badge>
            <span className="text-sm font-medium text-ink">{ENTITY_TYPE_LABELS[item.ref.entityType]}</span>
            <Badge tone={CONFIDENCE_TONES[item.confidence]}>{CONFIDENCE_LABELS[item.confidence]}</Badge>
            <span className="text-xs uppercase tracking-wide text-ink-muted">{item.role}</span>
          </div>
          <p className="mt-1 text-sm text-ink">{item.label}</p>
          <p className="mt-0.5 font-mono text-xs text-ink-muted">
            <button
              type="button"
              className="hover:text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
              title={item.ref.externalId}
              onClick={() => setRevealed((v) => !v)}
            >
              {revealed ? item.ref.externalId : maskId(item.ref.externalId)}
            </button>
            <span className="sr-only"> — click to {revealed ? 'mask' : 'reveal'} full id</span>
          </p>
          {item.warnings && item.warnings.length > 0 && (
            <ul className="mt-2 space-y-1">
              {item.warnings.map((w) => (
                <li key={w} className="flex items-start gap-1.5 text-[13px] text-warning">
                  <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className="mt-2 text-[13px] font-medium text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
            onClick={() => setWhyOpen((v) => !v)}
            aria-expanded={whyOpen}
          >
            {whyOpen ? 'Hide evidence' : 'Why?'}
          </button>
          {whyOpen && (
            <ul className="mt-2 space-y-1.5 border-t border-line pt-2">
              {item.evidence.map((e) => (
                <li key={`${e.code}-${e.text}`} className="text-[13px] text-ink-secondary">
                  <span className="font-mono text-xs text-ink-muted">{e.code}</span> {e.text}
                </li>
              ))}
              {item.evidence.length === 0 && <li className="text-[13px] text-ink-muted">No evidence listed.</li>}
            </ul>
          )}
          {needsReason && canLink && (
            <FormField label="Reason for LOW confidence" htmlFor={`low-${itemKey(item.ref)}`} required className="mt-3">
              <Input
                id={`low-${itemKey(item.ref)}`}
                value={lowReason}
                onChange={(e) => onLowReason(e.target.value)}
                placeholder="Why confirm this low-confidence identity?"
                maxLength={500}
              />
            </FormField>
          )}
        </div>
      </div>
    </div>
  );
}

export function CandidateReviewPanel({ group, onClose, onChanged }: CandidateReviewPanelProps) {
  const can = useCan();
  const canLink = can('production.link');
  const { notify } = useToast();
  const formId = useId();

  const allItems = useMemo(() => [...group.items, ...group.conflicts], [group]);

  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const item of allItems) {
      init[itemKey(item.ref)] = item.preselected && item.confidence === 'high';
    }
    return init;
  });
  const [lowReasons, setLowReasons] = useState<Record<string, string>>({});
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const [classifyTarget, setClassifyTarget] = useState<ClassifyTarget>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [createName, setCreateName] = useState(group.proposedName);
  const [createCategory, setCreateCategory] = useState(group.proposedCategory);
  const [createCountry, setCreateCountry] = useState('JO');
  const [createCity, setCreateCity] = useState('');
  const [createContact, setCreateContact] = useState('');
  const [createPhone, setCreatePhone] = useState('');

  const [businessSearch, setBusinessSearch] = useState('');
  const [businessOptions, setBusinessOptions] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [existingBusinessId, setExistingBusinessId] = useState(group.linkedBusinessId ?? '');
  const [classifyReason, setClassifyReason] = useState('');
  const [classifyRefKey, setClassifyRefKey] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusinessId, setRejectBusinessId] = useState(group.linkedBusinessId ?? '');

  useEffect(() => {
    if (confirmMode !== 'existing' && !rejectOpen) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      void listPharmacies({ search: businessSearch, page: 1, pageSize: 25, sort: 'name', order: 'asc' })
        .then((res) => {
          if (!cancelled) setBusinessOptions(res.items.map((p) => ({ id: p.id, name: p.name, code: p.code })));
        })
        .catch(() => {
          if (!cancelled) setBusinessOptions([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [businessSearch, confirmMode, rejectOpen]);

  const selectedRefs = (): Array<{ ref: ProductionEntityRef; item: CandidateItem }> =>
    allItems
      .filter((item) => selected[itemKey(item.ref)] && item.confidence !== 'conflict')
      .map((item) => ({ ref: item.ref, item }));

  const selectionValid = (): string | null => {
    const picks = selectedRefs();
    if (picks.length === 0) return 'Select at least one identity to confirm.';
    for (const { item } of picks) {
      if (item.confidence === 'medium' && !selected[itemKey(item.ref)]) {
        return 'MEDIUM confidence items need an explicit tick.';
      }
      if (item.confidence === 'low' && !(lowReasons[itemKey(item.ref)] ?? '').trim()) {
        return `LOW confidence requires a reason for ${item.label}.`;
      }
    }
    return null;
  };

  const buildSelectedPayload = () =>
    selectedRefs().map(({ ref, item }) => ({
      ref,
      role: item.role,
      expectedConfidence: item.confidence,
      lowReason: item.confidence === 'low' ? lowReasons[itemKey(ref)]?.trim() : undefined,
    }));

  const runConfirm = async (e: FormEvent) => {
    e.preventDefault();
    const invalid = selectionValid();
    if (invalid) {
      setSubmitError(invalid);
      return;
    }
    if (confirmMode === 'create' && (!createName.trim() || !createCity.trim() || !createContact.trim() || !createPhone.trim())) {
      setSubmitError('Name, city, contact and phone are required to create a business.');
      return;
    }
    if (confirmMode === 'existing' && !existingBusinessId) {
      setSubmitError('Select an existing pharmacy to link.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      const result = await confirmProductionLinks({
        groupKey: group.groupKey,
        selected: buildSelectedPayload(),
        ...(confirmMode === 'create'
          ? {
              createBusiness: {
                name: createName.trim(),
                category: createCategory,
                country: createCountry,
                city: createCity.trim(),
                contactPerson: createContact.trim(),
                phone: createPhone.trim(),
              },
            }
          : { existingBusinessId }),
      });
      notify({
        tone: 'success',
        title: result.createdBusiness ? 'Business created and links confirmed' : 'Links confirmed',
        description: `${result.linkIds.length} identity link${result.linkIds.length === 1 ? '' : 's'} saved.`,
      });
      onChanged();
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Could not confirm links.'));
      setBusy(false);
    }
  };

  const runClassify = async (e: FormEvent) => {
    e.preventDefault();
    if (!classifyTarget || !classifyRefKey || !classifyReason.trim()) {
      setSubmitError('Choose an identity and provide a reason.');
      return;
    }
    const item = allItems.find((i) => itemKey(i.ref) === classifyRefKey);
    if (!item) {
      setSubmitError('Identity not found.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      await classifyProductionRecord({
        ref: item.ref,
        classification: classifyTarget,
        reason: classifyReason.trim(),
      });
      notify({
        tone: 'success',
        title: `Marked as ${classifyTarget}`,
        description: item.label,
      });
      onChanged();
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Could not classify record.'));
      setBusy(false);
    }
  };

  const runReject = async (e: FormEvent) => {
    e.preventDefault();
    const picks = selectedRefs();
    if (picks.length === 0) {
      setSubmitError('Select at least one identity to reject.');
      return;
    }
    if (!rejectBusinessId) {
      setSubmitError('Select the pharmacy these identities were considered against.');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      await rejectProductionLinks({
        businessId: rejectBusinessId,
        refs: picks.map((p) => p.ref),
        reason: rejectReason.trim() || undefined,
      });
      notify({ tone: 'success', title: 'Identities rejected', description: 'Rejection recorded against the selected pharmacy.' });
      onChanged();
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Could not reject identities.'));
      setBusy(false);
    }
  };

  const onLeaveUnresolved = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      await leaveProductionUnresolved();
      notify({ tone: 'info', title: 'Left unresolved', description: 'No changes were written. The candidate remains in the list.' });
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err, 'Could not leave unresolved.'));
      setBusy(false);
    }
  };

  const footer = (() => {
    if (confirmMode || classifyTarget || rejectOpen) {
      return (
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmMode(null);
              setClassifyTarget(null);
              setRejectOpen(false);
              setSubmitError(null);
            }}
            disabled={busy}
          >
            Back
          </Button>
          {confirmMode && (
            <Button type="submit" form={`${formId}-confirm`} loading={busy}>
              {confirmMode === 'create' ? 'Create & Confirm' : 'Confirm Links'}
            </Button>
          )}
          {classifyTarget && (
            <Button type="submit" form={`${formId}-classify`} loading={busy}>
              Mark as {classifyTarget === 'test' ? 'Test' : 'Developer'}
            </Button>
          )}
          {rejectOpen && (
            <Button type="submit" form={`${formId}-reject`} variant="danger" loading={busy}>
              Reject
            </Button>
          )}
        </>
      );
    }
    return (
      <>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Close
        </Button>
        {canLink && (
          <>
            <Button variant="ghost" onClick={() => void onLeaveUnresolved()} disabled={busy}>
              Leave Unresolved
            </Button>
            <Button variant="ghost" onClick={() => setRejectOpen(true)} disabled={busy}>
              Reject
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setClassifyTarget('test');
                setClassifyRefKey(allItems[0] ? itemKey(allItems[0].ref) : '');
              }}
              disabled={busy || allItems.length === 0}
            >
              Classify Test / Dev
            </Button>
            <Button variant="secondary" onClick={() => setConfirmMode('existing')} disabled={busy}>
              Link Existing
            </Button>
            <Button onClick={() => setConfirmMode('create')} disabled={busy}>
              Create Business & Confirm
            </Button>
          </>
        )}
      </>
    );
  })();

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={group.proposedName}
      description={`${CATEGORY_LABELS[group.proposedCategory]} · ${group.groupKey}`}
      icon={<Link2 size={18} aria-hidden="true" />}
      titleAside={
        group.linkedBusinessId ? (
          <Badge tone="success" dot>
            Linked
          </Badge>
        ) : group.conflicts.length > 0 ? (
          <Badge tone="danger" dot>
            Conflicts
          </Badge>
        ) : null
      }
      dismissible={!busy}
      footer={footer}
    >
      {submitError && (
        <p role="alert" className="mb-4 rounded-md border border-danger/20 bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {submitError}
        </p>
      )}

      {!canLink && (
        <p className="mb-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-[13px] text-ink-secondary">
          You can review candidates. Confirming, classifying or rejecting requires the production.link permission.
        </p>
      )}

      {confirmMode === null && !classifyTarget && !rejectOpen && (
        <div className="space-y-6">
          {group.linkedBusinessId && (
            <p className="text-sm text-ink-secondary">
              Already linked to{' '}
              <Link to={`/pharmacies/${group.linkedBusinessId}`} className="font-medium text-primary hover:underline">
                pharmacy
              </Link>
              .
            </p>
          )}

          <section>
            <h3 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-ink-muted">Identities</h3>
            <div className="space-y-2">
              {group.items.map((item) => (
                <SelectionRow
                  key={itemKey(item.ref)}
                  item={item}
                  checked={!!selected[itemKey(item.ref)]}
                  disabled={busy}
                  lowReason={lowReasons[itemKey(item.ref)] ?? ''}
                  canLink={canLink}
                  onToggle={(next) => setSelected((s) => ({ ...s, [itemKey(item.ref)]: next }))}
                  onLowReason={(value) => setLowReasons((r) => ({ ...r, [itemKey(item.ref)]: value }))}
                />
              ))}
              {group.items.length === 0 && <p className="text-sm text-ink-muted">No candidate identities in this group.</p>}
            </div>
          </section>

          {group.conflicts.length > 0 && (
            <section>
              <h3 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-danger">Conflicts</h3>
              <div className="space-y-2">
                {group.conflicts.map((item) => (
                  <SelectionRow
                    key={itemKey(item.ref)}
                    item={item}
                    checked={false}
                    disabled
                    lowReason=""
                    canLink={canLink}
                    onToggle={() => undefined}
                    onLowReason={() => undefined}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {confirmMode === 'create' && (
        <form id={`${formId}-confirm`} noValidate onSubmit={runConfirm} className="space-y-4">
          <p className="text-sm text-ink-secondary">
            Creates a Central Admin business and confirms the selected identities in one step. Contact details can be completed later.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Name" htmlFor={`${formId}-name`} required className="sm:col-span-2">
              <Input id={`${formId}-name`} value={createName} onChange={(e) => setCreateName(e.target.value)} maxLength={120} />
            </FormField>
            <FormField label="Category" htmlFor={`${formId}-category`} required>
              <Select
                id={`${formId}-category`}
                value={createCategory}
                onChange={(e) => setCreateCategory(e.target.value as typeof createCategory)}
                options={(Object.keys(CATEGORY_LABELS) as Array<keyof typeof CATEGORY_LABELS>).map((k) => ({
                  value: k,
                  label: CATEGORY_LABELS[k],
                }))}
              />
            </FormField>
            <FormField label="Country" htmlFor={`${formId}-country`} required>
              <Select
                id={`${formId}-country`}
                value={createCountry}
                onChange={(e) => setCreateCountry(e.target.value)}
                options={[...COUNTRY_OPTIONS]}
              />
            </FormField>
            <FormField label="City" htmlFor={`${formId}-city`} required>
              <Input id={`${formId}-city`} value={createCity} onChange={(e) => setCreateCity(e.target.value)} maxLength={80} />
            </FormField>
            <FormField label="Contact person" htmlFor={`${formId}-contact`} required>
              <Input id={`${formId}-contact`} value={createContact} onChange={(e) => setCreateContact(e.target.value)} maxLength={120} />
            </FormField>
            <FormField label="Phone" htmlFor={`${formId}-phone`} required>
              <Input id={`${formId}-phone`} value={createPhone} onChange={(e) => setCreatePhone(e.target.value)} maxLength={32} />
            </FormField>
          </div>
          <p className="text-xs text-ink-muted">{selectedRefs().length} identit{selectedRefs().length === 1 ? 'y' : 'ies'} will be confirmed.</p>
        </form>
      )}

      {confirmMode === 'existing' && (
        <form id={`${formId}-confirm`} noValidate onSubmit={runConfirm} className="space-y-4">
          <FormField label="Search pharmacies" htmlFor={`${formId}-biz-search`}>
            <Input
              id={`${formId}-biz-search`}
              value={businessSearch}
              onChange={(e) => setBusinessSearch(e.target.value)}
              placeholder="Search by name or code…"
            />
          </FormField>
          <FormField label="Pharmacy" htmlFor={`${formId}-biz`} required>
            <Select
              id={`${formId}-biz`}
              value={existingBusinessId}
              onChange={(e) => setExistingBusinessId(e.target.value)}
              placeholder="Select pharmacy"
              options={businessOptions.map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
            />
          </FormField>
          <p className="text-xs text-ink-muted">{selectedRefs().length} identit{selectedRefs().length === 1 ? 'y' : 'ies'} will be confirmed.</p>
        </form>
      )}

      {classifyTarget && (
        <form id={`${formId}-classify`} noValidate onSubmit={runClassify} className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={classifyTarget === 'test' ? 'primary' : 'secondary'}
              onClick={() => setClassifyTarget('test')}
            >
              Test
            </Button>
            <Button
              type="button"
              size="sm"
              variant={classifyTarget === 'developer' ? 'primary' : 'secondary'}
              onClick={() => setClassifyTarget('developer')}
            >
              Developer
            </Button>
          </div>
          <FormField label="Identity" htmlFor={`${formId}-classify-ref`} required>
            <Select
              id={`${formId}-classify-ref`}
              value={classifyRefKey}
              onChange={(e) => setClassifyRefKey(e.target.value)}
              options={allItems.map((i) => ({
                value: itemKey(i.ref),
                label: `${ENTITY_TYPE_LABELS[i.ref.entityType]} — ${i.label}`,
              }))}
            />
          </FormField>
          <FormField
            label="Reason"
            htmlFor={`${formId}-classify-reason`}
            required
            error={undefined}
            hint="Required. Classification moves the record to the Excluded tab."
          >
            <Input
              id={`${formId}-classify-reason`}
              value={classifyReason}
              onChange={(e) => setClassifyReason(e.target.value)}
              maxLength={500}
              aria-describedby={fieldDescribedBy(`${formId}-classify-reason`, { hint: true })}
            />
          </FormField>
        </form>
      )}

      {rejectOpen && (
        <form id={`${formId}-reject`} noValidate onSubmit={runReject} className="space-y-4">
          <p className="text-sm text-ink-secondary">
            Records a rejection against a pharmacy for the selected identities. Select the identities above before opening Reject.
          </p>
          <FormField label="Search pharmacies" htmlFor={`${formId}-rej-search`}>
            <Input id={`${formId}-rej-search`} value={businessSearch} onChange={(e) => setBusinessSearch(e.target.value)} placeholder="Search…" />
          </FormField>
          <FormField label="Pharmacy" htmlFor={`${formId}-rej-biz`} required>
            <Select
              id={`${formId}-rej-biz`}
              value={rejectBusinessId}
              onChange={(e) => setRejectBusinessId(e.target.value)}
              placeholder="Select pharmacy"
              options={businessOptions.map((b) => ({ value: b.id, label: `${b.name} (${b.code})` }))}
            />
          </FormField>
          <FormField label="Reason (optional)" htmlFor={`${formId}-rej-reason`}>
            <Input id={`${formId}-rej-reason`} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500} />
          </FormField>
        </form>
      )}
    </Modal>
  );
}
